import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Camera, Users, Globe } from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { setPageMeta, JsonLd, breadcrumbSchema } from "../lib/schema";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Authorised sources only",
    body:
      "We buy from brand boutiques and authorised UK retailers. Never grey-market, never resale.",
  },
  {
    icon: Camera,
    title: "Photographed at every stage",
    body:
      "Inside the boutique. At our UK consolidation point. Before international dispatch. You see the journey.",
  },
  {
    icon: Users,
    title: "Real human concierges",
    body:
      "Every order is read, quoted, and handled by a real person in London. Never an automated forwarder.",
  },
  {
    icon: Globe,
    title: "All 36 states + FCT",
    body:
      "From Lagos to Sokoto, Port Harcourt to Borno — full national coverage. Always.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    setPageMeta({
      title: "About My Private Shopper | UK to Nigeria Concierge",
      description:
        "My Private Shopper (MPS) is a London-based UK-to-Nigeria personal shopping concierge, operating privately since 2011 and formally UK-registered in 2023. 500+ orders shipped, all 36 states covered.",
      canonical: "https://privateshopper.co.uk/about",
    });
  }, []);

  return (
    <>
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "About" },
      ])} />
      <PageHeader
        overline="About"
        title={<>The London concierge for <span className="italic text-[#C9A96E]">discerning Nigerian shoppers</span>.</>}
        body="We started MPS privately in 2011 and formally registered the business in the UK in 2023 — because the existing options (Nigerian-card-blind checkouts, faceless freight forwarders, overpriced couriers) were failing the diaspora and the home market alike."
      />

      {/* Story */}
      <section className="bg-[#F8F5F0] py-24 md:py-32">
        <div className="container-mps grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.img
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            src="https://images.unsplash.com/photo-1627245084666-199325217d5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBtYXlmYWlyJTIwc3RyZWV0fGVufDB8fHx8MTc4MTM0NTQ1MHww&ixlib=rb-4.1.0&q=85"
            alt="London street"
            className="lg:col-span-5 w-full h-[520px] object-cover"
          />
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="overline text-[#C9A96E] mb-4">Our Story</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              Built for the way <span className="italic text-[#C9A96E]">Nigerians actually shop</span>.
            </h2>
            <div className="gold-divider mt-8" />
            <div className="mt-8 space-y-6 text-[#6B7280] leading-relaxed">
              <p>
                Most &ldquo;buying agents&rdquo; treat their clients as parcels. We treat ours as the
                affluent, demanding shoppers they are. Our service is built around three
                non-negotiables: authentic sourcing, full documentation, and a single
                accountable concierge for every order.
              </p>
              <p>
                What began in 2011 as a small circle of private clients — friends, family, and referrals
                who trusted us to shop the UK on their behalf — has grown into a fully UK-registered
                concierge business (as of 2023) with 500+ orders shipped across all 36 Nigerian states.
                We work with private clients, family offices, and a select portfolio of corporate B2B
                accounts.
              </p>
              <p>
                We do not run flash sales. We do not operate a marketplace. We do not
                forward parcels we have not inspected. We are simply your private
                shopper in London — calm, careful, and quietly accountable.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-[#0A0A0A] text-[#F8F5F0] py-24">
        <div className="container-mps">
          <div className="overline text-[#C9A96E] mb-4">Our Commitments</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-3xl">
            Four standards we will not compromise on.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-16">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="border-l border-[#C9A96E]/40 pl-6"
                data-testid={`about-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p.icon className="text-[#C9A96E] mb-5" size={28} strokeWidth={1.4} />
                <h3 className="font-serif text-2xl md:text-3xl mb-3">{p.title}</h3>
                <p className="text-[#F8F5F0]/70 leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F5F0] py-24 text-center">
        <div className="container-mps">
          <h2 className="font-serif text-4xl md:text-5xl">Let&apos;s start the conversation.</h2>
          <p className="mt-6 text-[#6B7280] max-w-xl mx-auto">
            Send us your first request or simply ask a question. Real reply, real fast.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/order"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#0A0A0A] text-[#F8F5F0] text-sm uppercase tracking-[0.18em] hover:bg-[#1A1A1A]"
            >
              Place an Order
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 border border-[#1A1A1A] text-sm uppercase tracking-[0.18em] hover:bg-[#0A0A0A] hover:text-[#F8F5F0]"
            >
              Contact MPS
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
